import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import { FormControl, TextField, Autocomplete, Box } from "@mui/material";
import { withTranslation } from "react-i18next";

import FileInput from "./FileInput";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { applicationTypes } from "common/constant";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import { getImageUrl } from "utils";
import getFileExtension from "utils/getFileExtension";

import pdfImg from "assets/images/pdf.png";
import docxImg from "assets/images/docx.png";

const NAME_TRANSFER_REASON_DESCRIPTION =
  "Please select the reason for name change.";

const PROOF_OF_OWNERSHIP_DESCRIPTION =
  "Any of the following documents shall be considered as acceptable proof of ownership or occupancy of premises for which connection is required:";

const fileTypes = ["pdf", "docx", "doc"];

const isValidFileType = (fileName) => {
  const fileExtension = getFileExtension(fileName).toLowerCase();
  return fileTypes.includes(fileExtension);
};

const DocumentCategory = ({
  t,
  category: { documentCategoryDescription, documentTypes, documentCategoryId },
  formik,
  isError,
}) => {
  const {
    values: { documents, applicationTypeId, utilities = [], utilityId },
    setFieldValue,
  } = formik;

  const [selectedOption, setSelectedOption] = useState(null);

  const isNameTransferApplication = useMemo(
    () => applicationTypes["Name Change/Transfer"] === applicationTypeId,
    [applicationTypeId]
  );

  const isReasonField = useMemo(
    () =>
      documentCategoryDescription === NAME_TRANSFER_REASON_DESCRIPTION &&
      isNameTransferApplication,
    [documentCategoryDescription, isNameTransferApplication]
  );

  const isProofOfOwnership = useMemo(
    () => documentCategoryDescription === PROOF_OF_OWNERSHIP_DESCRIPTION,
    [documentCategoryDescription]
  );

  const updatedDocumentTypes = useMemo(
    () =>
      isProofOfOwnership
        ? [
            ...documentTypes,
            {
              documentTypeId: 0,
              documentTypeDescription: "Other",
              utilities: utilityId
                ? [utilityId]
                : utilities.map(({ value }) => value),
            },
          ]
        : documentTypes,
    [documentTypes, utilities, utilityId]
  );

  const initialOption = useMemo(
    () =>
      updatedDocumentTypes.find((document) =>
        documents.find(
          (formikDocument) =>
            formikDocument.documentTypeId === document.documentTypeId
        )
      ),
    [updatedDocumentTypes, documents]
  );

  const options = useMemo(
    () =>
      updatedDocumentTypes.map((document) => ({
        value: document.documentTypeId,
        label: document.documentTypeDescription,
        parentId: document.parentId,
        documentCategoryId: documentCategoryId,
        utilities: document.utilities,
        imageFileSize: document.imageFileSize,
        documentFileSize: document.documentFileSize,
      })),
    [updatedDocumentTypes]
  );

  const formikDocument = useMemo(
    () =>
      documents.find(
        (document) => document?.documentTypeId === selectedOption?.value
      ),
    [documents, selectedOption]
  );

  const isRequiredDocument = useMemo(
    () =>
      getIsDocumentRequired(documentTypes[0].documentRequiredEnum) ||
      isReasonField,
    [documentTypes[0].documentRequiredEnum, isReasonField]
  );

  useEffect(() => {
    if (initialOption) {
      setSelectedOption({
        label: initialOption.documentTypeDescription,
        value: initialOption.documentTypeId,
        utilities: initialOption.utilities,
        imageFileSize: initialOption.imageFileSize,
        documentFileSize: initialOption.documentFileSize,
      });
    }
  }, [initialOption]);

  const documentCategoryChangeHandler = (selectedDocumentType) => {
    const updatedDocuments = documents?.filter(
      (document) =>
        document.documentCategoryId !== selectedDocumentType?.documentCategoryId
    );

    // for making proofOfOwnershipDescription's value empty when its documentType changes.
    if (isProofOfOwnership) setFieldValue("proofOfOwnershipDescription", "");
    setFieldValue("documents", updatedDocuments);
    setSelectedOption(selectedDocumentType);
    removeSelectedFile();
  };

  const removeSelectedFile = () => {
    setFieldValue(
      "documents",
      documents.filter(
        (document) =>
          !options.map(({ value }) => value).includes(document?.documentTypeId)
      )
    );
  };

  return (
    <>
      <Row className="mb-2">
        <Col sm={6} className="mb-2">
          <p className="m-0">
            {documentCategoryDescription}
            {isRequiredDocument && <span className="text-danger">*</span>}
          </p>
        </Col>

        <Col sm={3} className="mb-2">
          {isReasonField ? (
            <SelectField
              label={t("Reason for Name Change") + "*"}
              name="parentId"
              formik={formik}
              options={options || []}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
            />
          ) : (
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                options={options}
                renderInput={(params) => (
                  <TextField {...params} label={t("Document Type")} />
                )}
                value={selectedOption || ""}
                onChange={(_, option) => documentCategoryChangeHandler(option)}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  );
                }}
                disableClearable
              />
            </FormControl>
          )}
        </Col>

        <Col sm={3} className="mb-2">
          {formikDocument ? (
            <>
              {isValidFileType(
                formikDocument.file?.name ?? formikDocument.documentURL
              ) ? (
                <div className="image-container">
                  <a
                    href={
                      formikDocument.url ||
                      getImageUrl(formikDocument.documentURL)
                    }
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Box
                      component="img"
                      className="image"
                      alt={formikDocument.file?.name || "document image"}
                      src={
                        getFileExtension(
                          formikDocument.file?.name ??
                            formikDocument.documentURL
                        ).toLowerCase() === "pdf"
                          ? pdfImg
                          : docxImg
                      }
                      sx={{ width: "80px" }}
                    />
                  </a>
                  <i
                    className="ri-close-circle-line fs-3 close-btn"
                    onClick={() =>
                      setFieldValue(
                        "documents",
                        documents.filter(
                          (document) =>
                            document?.documentTypeId !==
                            formikDocument.documentTypeId
                        )
                      )
                    }
                  />
                </div>
              ) : (
                <div className="image-container">
                  <Box
                    component="img"
                    className="image"
                    alt={formikDocument.file?.name || "document image"}
                    src={
                      formikDocument.url ||
                      getImageUrl(formikDocument.documentURL)
                    }
                  />
                  <i
                    className="ri-close-circle-line fs-3 close-btn"
                    onClick={() =>
                      setFieldValue(
                        "documents",
                        documents.filter(
                          (document) =>
                            document?.documentTypeId !==
                            formikDocument.documentTypeId
                        )
                      )
                    }
                  />
                </div>
              )}
            </>
          ) : (
            !isReasonField && (
              <FileInput
                setFieldValue={setFieldValue}
                documents={documents}
                parentId={selectedOption?.parentId}
                documentTypeId={selectedOption?.value}
                documentCategoryId={documentCategoryId}
                utilities={selectedOption?.utilities}
                isDisable={!selectedOption}
                isError={isError}
                documentRequiredEnum={documentTypes[0].documentRequiredEnum}
                imageFileSize={selectedOption?.imageFileSize}
                documentFileSize={selectedOption?.documentFileSize}
              />
            )
          )}
        </Col>
      </Row>
      {selectedOption?.value === 0 && isProofOfOwnership && (
        <Row className="mb-4 me-2">
          <InputFieldText
            label={t("Proof of Ownership Description") + "*"}
            name="proofOfOwnershipDescription"
            formik={formik}
            type="text"
            placeholder="Proof of Ownership Description"
          />
        </Row>
      )}
    </>
  );
};

DocumentCategory.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  documentTypes: PropTypes.array,
  category: PropTypes.object,
  isError: PropTypes.bool,
  isPartiallyUploading: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(DocumentCategory);
